/**
 * @auth 赖传峰
 * @date 2022/4/26
 */
import {
  computed,
  type ComputedRef,
  ref,
  type Ref,
  useCookie,
  useI18n,
  useRequestHeaders,
  useRoute,
  watch,
} from '#imports'
import $track from '@/track/webOfficialWebsite'
import { isPikPakClient } from '@/utils'
import qs from 'qs'
import UAParser from 'ua-parser-js'
import useDomain from '~/composables/useDomain'
import usePathNoLocale from '~/composables/usePathNoLocale'
import useShareId from '~/composables/useShareId'
import { cookieAttribution, storageReferer } from '~/middleware/attribution.global'
import { DEEP_LINK_PREFIX } from '~/types/constants'
import { AppInstallPlatform, OSName, RoutePath, StorageKey, SupportLocale } from '~/types/enum'
import apiPrefix from '~/utils/apiPrefix'
import installChannelIcons, { ChannelCategory } from '~/utils/installChannelIcons'

export const osNameInServerAndClient = () =>
  import.meta.server ? UAParser(useRequestHeaders(['user-agent'])['user-agent']).os.name : new UAParser().getOS().name

/* 考虑到 Apple Silicon 推出四年，市面上大部分电脑应该都是 Apple Silicon，所以默认值为 true */
export const getIsAppleSilicon = async (): Promise<boolean> => {
  if (import.meta.server) return true
  try {
    // @ts-expect-error https://developer.mozilla.org/zh-CN/docs/Web/API/NavigatorUAData/getHighEntropyValues
    const { architecture } = await navigator.userAgentData.getHighEntropyValues(['architecture'])
    return architecture === 'arm'
  } catch {
    return true
  }
}

export type SourceAttribution = 'websidebar' | 'webshare' | 'official'

const appInstallLinkFromServer = (platform: AppInstallPlatform, source?: SourceAttribution): string => {
  // 获取当前域名
  const domain = useDomain().value
  // 获取当前路由
  const route = useRoute()
  // FIX: server 端拿不到 referrer, 需要在 client 端在重新获取一次
  const referer: string[] = import.meta.server ? [] : storageReferer.get()
  // 拼接参数
  const qsString = qs.stringify(
    {
      pf: platform,
      rf: referer,
      rr: domain + route.path,
      tf: source,
    },
    { arrayFormat: 'comma' },
  ) as `pf=${AppInstallPlatform}&rf=${string}&tf=${string}`
  // 拼接下载链接名称
  const tailPathname = (() => {
    switch (platform) {
      case AppInstallPlatform.ANDROID_APK:
      case AppInstallPlatform.GOOGLE_PLAY:
        return 'PikPak.apk'
      case AppInstallPlatform.TV_APK:
        return 'PikPak_tv.apk'
      case AppInstallPlatform.WINDOWS:
        return 'official_PikPak.exe'
      default:
        return 'url'
    }
  })()
  // 拼接下载链接
  return `${apiPrefix(domain).PP_DRIVE_API}/package/v1/download/${tailPathname}?${qsString}`
}
export const appInstallLink = (
  platform: AppInstallPlatform,
  source: SourceAttribution = 'official',
  inSharePage?: boolean,
  extraDeeplink?: string,
): string => {
  // 获取当前 cookie 中的 source 归因
  const cookieSource = cookieAttribution.get().source
  // 如果当前 cookie 中没有 source 归因，全部走后端的下载链接
  if (!cookieSource) {
    // 谷歌下载链接 特殊处理
    if (platform === AppInstallPlatform.GOOGLE_PLAY && inSharePage) {
      // gp包，且在分享页重，
      const shareId = useShareId()
      const passCode = useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`).value || ''
      const deeplinkReferrer = `/drive/share_watch?from=${source}&share_id=${shareId.value}&pass_code=${
        passCode.length === 4 ? passCode : ''
      }`
      const deeplink = (
        import.meta.server ? Buffer.from(deeplinkReferrer).toString('base64') : window.btoa(deeplinkReferrer)
      )
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '')
      return `https://play.google.com/store/apps/details?id=com.pikcloud.pikpak&referrer=${deeplink}`
    }
    if (platform === AppInstallPlatform.GOOGLE_PLAY && extraDeeplink) {
      const deeplink = (import.meta.server ? Buffer.from(extraDeeplink).toString('base64') : window.btoa(extraDeeplink))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '')
      return `https://play.google.com/store/apps/details?id=com.pikcloud.pikpak&referrer=${deeplink}`
    }
    // 其他平台走后端的下载链接
    return appInstallLinkFromServer(platform, source)
  }

  const downloadOrigin = `https://download.${useDomain().value}`
  switch (platform) {
    case AppInstallPlatform.ANDROID_APK:
      return `${downloadOrigin}/app/${cookieSource}_PikPak.apk`
    case AppInstallPlatform.APPLE_STORE: {
      const { locale } = useI18n()
      const appStoreLocalePathMap: { [key in SupportLocale]: string } = {
        [SupportLocale.EN_US]: '/us',
        [SupportLocale.ES]: '/es',
        [SupportLocale.ID]: '/id',
        [SupportLocale.JA]: '/jp',
        [SupportLocale.KO]: '/kr',
        [SupportLocale.MS]: '/ms',
        [SupportLocale.PT]: '/pt',
        [SupportLocale.RU]: '/ru',
        [SupportLocale.UK]: '/ua',
        [SupportLocale.ZH_CN]: '/cn',
        [SupportLocale.ZH_TW]: '/tw',
      }
      return `https://apps.apple.com${appStoreLocalePathMap[locale.value as SupportLocale]}/app/apple-store/id1616861537?pt=124220499&ct=${cookieSource}&mt=8`
    }
    case AppInstallPlatform.GOOGLE_PLAY: {
      const shareId = useShareId()
      const passCode = useCookie(`${StorageKey.SHARE_PWD_CODE}_${shareId.value}`).value || ''
      let deeplinkReferrer = inSharePage
        ? `/drive/share_watch?from=${cookieSource}&share_id=${shareId.value}&pass_code=${
            passCode.length === 4 ? passCode : ''
          }`
        : `/xpan/main_tab?from=${cookieSource}`

      if (extraDeeplink) {
        deeplinkReferrer = extraDeeplink
      }

      const deeplink = (
        import.meta.server ? Buffer.from(deeplinkReferrer).toString('base64') : window.btoa(deeplinkReferrer)
      )
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '')
      return osNameInServerAndClient() === OSName.ANDROID
        ? `intent://details?id=com.pikcloud.pikpak&referrer=${deeplink};#Intent;scheme=market;package=com.pikcloud.pikpak;S.browser_fallback_url=https://play.google.com/store/apps/details?id=com.pikcloud.pikpak&referrer=${deeplink};end;`
        : `https://play.google.com/store/apps/details?id=com.pikcloud.pikpak&referrer=${deeplink}`
    }
    case AppInstallPlatform.TV_APK:
      return `${downloadOrigin}/app/${cookieSource}_tv_PikPak.apk`
    case AppInstallPlatform.WINDOWS:
      return `${downloadOrigin}/desktop/official_PikPak.exe`
    default:
      return appInstallLinkFromServer(platform, source)
  }
}

export const useAppInstallIcon = () => {
  const locale = useI18n().locale as ComputedRef<SupportLocale>

  const googlePlay = ref<string>()
  const androidApk = ref<string>()
  const appStore = ref<string>()
  const galaxyStore = ref<string>()

  watch(
    locale,
    async newLocale => {
      const icons = installChannelIcons(newLocale)
      googlePlay.value = await icons(ChannelCategory.GOOGLE_PLAY)
      androidApk.value = await icons(ChannelCategory.ANDROID)
      appStore.value = await icons(ChannelCategory.APP_STORE)
      galaxyStore.value = await icons(ChannelCategory.GALAXY_STORE)
    },
    { immediate: true },
  )

  return {
    googlePlay,
    androidApk,
    appStore,
    galaxyStore,
  }
}

export type InstallChannels = {
  href: string
  imgSrc: Ref<string | undefined>
  trackEventButtonParameter: Parameters<typeof $track.web_official_mobile_page_click>[0]['button']
  hide?: boolean
}[]

export default function useInstallChannels(source?: SourceAttribution) {
  // 获取当前路径，不包含语言
  const pathNoLocale = usePathNoLocale()
  // 获取安装渠道图标
  const { googlePlay, androidApk, galaxyStore, appStore } = useAppInstallIcon()

  // 是否安卓
  const isAndroid = osNameInServerAndClient() === OSName.ANDROID
  // 是否 iOS
  const isIOS = osNameInServerAndClient() === OSName.IOS
  // 是否电视页
  const isTVPage = pathNoLocale.value === RoutePath.TV_APP
  // 是否分享页
  const isSharePage = /^\/s/.test(pathNoLocale.value)

  // 谷歌下载链接
  const googlePlayLink = appInstallLink(AppInstallPlatform.GOOGLE_PLAY, source, isSharePage)
  // 安卓 apk 下载链接
  const mobileApkInstallLink = appInstallLink(AppInstallPlatform.ANDROID_APK, source)
  // 电视 apk 下载链接
  const tvApkInstallLink = appInstallLink(AppInstallPlatform.TV_APK, source)
  // iOS 下载链接
  const iOSInstallLink = appInstallLink(AppInstallPlatform.APPLE_STORE, source)
  // 三星应用商店下载链接
  const galaxyStoreInstallLink = 'https://galaxystore.samsung.com/detail/com.pikcloud.pikpak'

  // 是否是安卓PikPak客户端
  const isAndroidPikpakClient = isAndroid && isPikPakClient()

  // 返回安装渠道
  return computed<InstallChannels>(() => {
    if (isTVPage) {
      return [
        {
          href: googlePlayLink,
          imgSrc: googlePlay,
          trackEventButtonParameter: 'google',
        },
        {
          href: tvApkInstallLink,
          imgSrc: androidApk,
          trackEventButtonParameter: 'apk',
          hide: isAndroidPikpakClient, // 安卓 且 是PikPak客户端 隐藏
        },
      ]
    }

    if (isAndroid) {
      return [
        {
          href: googlePlayLink,
          imgSrc: googlePlay,
          trackEventButtonParameter: 'google',
        },
        {
          href: mobileApkInstallLink,
          imgSrc: androidApk,
          trackEventButtonParameter: 'apk',
          hide: isAndroidPikpakClient, // 安卓 且 是PikPak客户端 隐藏
        },
        {
          href: galaxyStoreInstallLink,
          imgSrc: galaxyStore,
          trackEventButtonParameter: 'samsung',
        },
        {
          href: iOSInstallLink,
          imgSrc: appStore,
          trackEventButtonParameter: 'app_store',
        },
      ]
    }
    if (isIOS) {
      return [
        {
          href: iOSInstallLink,
          imgSrc: appStore,
          trackEventButtonParameter: 'app_store',
        },
        {
          href: googlePlayLink,
          imgSrc: googlePlay,
          trackEventButtonParameter: 'google',
        },
        {
          href: mobileApkInstallLink,
          imgSrc: androidApk,
          trackEventButtonParameter: 'apk',
        },
        {
          href: galaxyStoreInstallLink,
          imgSrc: galaxyStore,
          trackEventButtonParameter: 'samsung',
        },
      ]
    }
    return [
      {
        href: googlePlayLink,
        imgSrc: googlePlay,
        trackEventButtonParameter: 'google',
      },
      {
        href: iOSInstallLink,
        imgSrc: appStore,
        trackEventButtonParameter: 'app_store',
      },
      {
        href: mobileApkInstallLink,
        imgSrc: androidApk,
        trackEventButtonParameter: 'apk',
        hide: isAndroidPikpakClient, // 安卓 且 是PikPak客户端 隐藏
      },
      {
        href: galaxyStoreInstallLink,
        imgSrc: galaxyStore,
        trackEventButtonParameter: 'samsung',
      },
    ]
  })
}

export const getAddUrlPikPakLink = (url: string) => {
  const encodeUrl = encodeURIComponent(url)
  return `${DEEP_LINK_PREFIX}/main_tab?tab=1&add_url=${encodeUrl}&from=`
}
